<template>
  <div class="proxima--font">
    <ManageBaseTableHeader :title="$route.name" :Model="Model" />
    <transition name="fade" mode="out-in">
      <v-data-table
        v-model="selected"
        :show-select="false"
        :headers="headers"
        :loading="loading"
        :items="tableItemModels"
        hide-default-footer
        :items-per-page="perPage"
        disable-sort
        :server-items-length="totalItems"
        :class="['mm-base-table', { sticky: !['xs'].includes($mq) }]"
        :search="tableSearchString"
        ref="table"
        height="calc(100vh - 230px)"
        mobile-breakpoint="0"
        v-bind="{ dense: true, fixedHeader: true, ...$attrs }"
      >
        <template v-slot:no-data>
          No records found here.
        </template>
        <template v-slot:item.expand="{ item }">
          <v-btn
            icon
            small
            append
            @click="openEditDialog(item.id)"
            title="Expand Record"
          >
            <v-icon small>
              mdi-arrow-expand
            </v-icon>
          </v-btn>
        </template>

        <!-- Orders View -->

        <template v-slot:item.name="{ item, header }">
          <AdminCellDialogText
            :value="item.name"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="name"
          />
        </template>

        <template v-slot:item.ticket_number="props">
          {{ props.item.ticket_number }}
        </template>

        <template v-slot:item.customer.full_name="{ item, header }">
          <AdminCellRelated
            :key="`${item.id}`"
            :id="item.id"
            :item="item"
            :header="header"
            :Model="Model"
            small
          />
        </template>
        <template v-slot:item.paid="{ item }">
          <AdminCellCheckbox :Model="Model" :id="item.id" field-name="paid" />
        </template>

        <template v-slot:item.ready_for_guest="{ item }">
          <AdminCellCheckbox
            :Model="Model"
            :id="item.id"
            field-name="ready_for_guest"
          />
        </template>

        <template v-slot:item.completed="{ item }">
          <AdminCellCheckbox
            :Model="Model"
            :id="item.id"
            field-name="completed"
          />
        </template>

        <template v-slot:item.is_gift="{ item, header }">
          <!-- <v-icon small v-if="item.is_gift">mdi-gift-outline</v-icon> -->
          <AdminCellGiftee
            :value="item.is_gift"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
          />
        </template>

        <template v-slot:item.availability.name="{ item, header }">
          <AdminCellRelated
            :key="`${item.id}`"
            :id="item.id"
            :item="item"
            :header="header"
            :Model="Model"
            small
          />
        </template>

        <template v-slot:item.grand_total_formatted="{ item }">
          {{
            Model.find(item.id) ? Model.find(item.id).grand_total_formatted : ''
          }}
        </template>

        <template v-slot:item.fulfillment_type="{ item, header }">
          <AdminCellSingleSelect
            :items="getFulfillmentOptions"
            :value="item.fulfillment_type"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="fulfillment_type"
          />
        </template>

        <template v-slot:item.fulfillment_slot="{ item, header }">
          <AdminCellFulfillmentSlotPicker
            :fulfillment-type="item.fulfillment_type"
            :value="item.fulfillment_slot"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="fulfillment_slot"
          />
        </template>

        <template v-slot:item.customer.email="{ item, header }">
          <AdminCellTextCopiable
            :value="_get(item, 'customer.email', null)"
            :width="header.width || null"
          />
        </template>

        <template v-slot:item.delivery_address="{ item }">
          <AdminCellCopyTextPopup
            :value="item.delivery_address"
            icon="mdi-map-marker-outline"
          />
        </template>

        <template v-slot:item.special_instructions="{ item }">
          <AdminCellNotesPopup :value="item.special_instructions" />
        </template>

        <template v-slot:item.time_ordered="{ item, header }">
          <AdminCellTimestampReadable
            :value="Model.find(item.id).time_ordered"
            :width="header.width || null"
            field-name="time_ordered"
          />
        </template>
        <!-- / Orders View -->

        <!-- Items View -->
        <template v-slot:item.stock="{ item, header }">
          <AdminCellDialogStockStepper
            :value="item.stock"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            min="0"
            field-name="stock"
          />
        </template>
        <template v-slot:item.description="{ item, header }">
          <AdminCellDialogTextMultiline
            :value="item.description"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="description"
          />
        </template>
        <template v-slot:item.unit="{ item, header }">
          <AdminCellDialogText
            :value="item.unit"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="unit"
          />
        </template>
        <template v-slot:item.price="{ item, header }">
          <AdminCellDialogPrice
            :value="item.price"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="price"
          />
        </template>
        <template v-slot:item.unlimited="{ item }">
          <AdminCellCheckbox
            :Model="Model"
            :id="item.id"
            field-name="unlimited"
          />
        </template>
        <template v-slot:item.hidden="{ item }">
          <AdminCellCheckbox :Model="Model" :id="item.id" field-name="hidden" />
        </template>
        <template v-slot:item.sold_out="{ item }">
          <AdminCellCheckbox
            :Model="Model"
            :id="item.id"
            field-name="sold_out"
          />
        </template>
        <template v-slot:item.section.name="{ item, header }">
          <AdminCellRelated
            :key="`${item.id}`"
            :id="item.id"
            :item="item"
            :header="header"
            :Model="Model"
            small
          />
        </template>
        <!-- /Items View -->

        <!-- Sections View -->
        <template v-slot:item.order="{ item, header }">
          <AdminCellDialogStockStepper
            :value="item.order"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            min="0"
            field-name="order"
          />
        </template>
        <!-- /Sections View -->

        <!-- Availability View -->
        <template v-slot:item.start_time="{ item }">
          <AdminCellDatetime
            :value="item.start_time"
            :Model="Model"
            :id="item.id"
            field-name="start_time"
          />
        </template>
        <template v-slot:item.end_time="{ item }">
          <AdminCellDatetime
            :value="item.end_time"
            :Model="Model"
            :id="item.id"
            field-name="end_time"
          />
        </template>
        <template v-slot:item.delivery_slots="{ item, header }">
          <AdminCellMultiselect
            :items="getDeliverySlots"
            :value="item.delivery_slots"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="delivery_slots"
          />
        </template>
        <template v-slot:item.pickup_slots="{ item, header }">
          <AdminCellMultiselect
            :items="getPickupSlots"
            :value="item.pickup_slots"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="pickup_slots"
          />
        </template>

        <!-- /Availability View -->

        <!-- Customer View -->
        <template v-slot:item.family_discount="{ item, header }">
          <AdminCellDialogPercentage
            :value="item.family_discount"
            :formatted-value="_get(item, header.formatted_value, null)"
            :width="header.width || null"
            :Model="Model"
            :id="item.id"
            field-name="family_discount"
          />
        </template>
        <template v-slot:item.email="{ item, header }">
          <AdminCellTextCopiable
            :value="_get(item, 'email', null)"
            :width="header.width || null"
          />
        </template>
        <template v-slot:item.address="{ item, header }">
          <AdminCellTextCopiable
            :value="_get(item, 'address', null)"
            :width="header.width || null"
          />
        </template>
        <template v-slot:item.notes="{ item }">
          <AdminCellNotesPopup :value="_get(item, 'notes', null)" />
        </template>
        <template v-slot:footer>
          <ManageBaseTableFooter @add="insertEntity" />
        </template>
        <!-- /Customer View -->
      </v-data-table>
    </transition>
    <DialogRecordDetail
      v-if="!!editId"
      :show="!!editId"
      @click:outside="closeEditDialog"
      @close="closeEditDialog"
      @save="createOrUpdateRecord(editId)"
      @update="updateEntity(editId, $event)"
      @create-order="createNewOrder"
      @update-cart="updateCart"
      @delete="deleteEntity(editId)"
      :saving="saving"
      :loading="loading"
      :errors="formErrors"
      :Model="Model"
      :id="editId"
    />
  </div>
</template>

<script>
import _get from 'lodash/get'

import { mapGetters, mapActions } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import { Model } from '@vuex-orm/core'
import CartModel from '@/models/CartModel'

import DialogRecordDetail from '@/components/DialogRecordDetail'

import ManageBaseTableHeader from '@/components/ManageBaseTableHeader'
import ManageBaseTableFooter from '@/components/ManageBaseTableFooter'
import AdminCellCheckbox from '@/components/AdminCellCheckbox'
import AdminCellMultiselect from '@/components/AdminCellMultiselect'
import AdminCellSingleSelect from '@/components/AdminCellSingleSelect'
import AdminCellDatetime from '@/components/AdminCellDatetime'
import AdminCellTimestampReadable from '@/components/AdminCellTimestampReadable'
import AdminCellDialogPrice from '@/components/AdminCellDialogPrice'
import AdminCellDialogText from '@/components/AdminCellDialogText'
import AdminCellDialogTextMultiline from '@/components/AdminCellDialogTextMultiline'
import AdminCellDialogPercentage from '@/components/AdminCellDialogPercentage'
import AdminCellGiftee from '@/components/AdminCellGiftee'
import AdminCellNotesPopup from '@/components/AdminCellNotesPopup'
import AdminCellCopyTextPopup from '@/components/AdminCellCopyTextPopup'
import AdminCellTextCopiable from '@/components/AdminCellTextCopiable'
import AdminCellFulfillmentSlotPicker from '@/components/AdminCellFulfillmentSlotPicker'
import AdminCellRelated from '@/components/AdminCellRelated'
import AdminCellDialogStockStepper from '@/components/AdminCellDialogStockStepper'

export default {
  name: 'ManageBaseTable',
  components: {
    DialogRecordDetail,
    ManageBaseTableHeader,
    ManageBaseTableFooter,
    AdminCellCheckbox,
    AdminCellMultiselect,
    AdminCellSingleSelect,
    AdminCellDialogPrice,
    AdminCellDialogStockStepper,
    AdminCellDialogText,
    AdminCellDialogTextMultiline,
    AdminCellDialogPercentage,
    AdminCellGiftee,
    AdminCellDatetime,
    AdminCellTimestampReadable,
    AdminCellNotesPopup,
    AdminCellCopyTextPopup,
    AdminCellTextCopiable,
    AdminCellFulfillmentSlotPicker,
    AdminCellRelated,
  },
  props: {
    Model: {
      type: Model,
      default: () => Model,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: [],
      editId: null,
      showNewRecordDialog: false,
      formErrors: {},
      saving: false,
    }
  },
  computed: {
    ...mapGetters('shop', [
      'getDeliverySlots',
      'getPickupSlots',
      'getFulfillmentOptions',
      'getShopId',
    ]),
    ...mapFields('admin', [
      'tableItems',
      'tableSearchString',
      'tableSortDesc',
      'isTableStale',
    ]),
    ...mapFields('admin', {
      perPage: 'tableMeta.per_page',
      totalItems: 'tableMeta.total',
    }),
    tableItemModels() {
      return this.Model.query()
        .whereIdIn(this.tableItems.map((i) => i.id))
        .with(this.Model.index_query_with ? this.Model.index_query_with() : '')
        .get()
    },
  },
  methods: {
    ...mapActions('cart', ['createOrderForAdmin']),

    saveText(id, fieldName, val) {
      if ('function' === typeof this.Model.updateModel) {
        const fields = { [fieldName]: val }
        this.Model.updateModel(id, fields)
      }
    }, // saveText

    async insertEntity() {
      let newRecord = await this.Model.new()

      this.Model.insertOrUpdate({
        data: { ...newRecord, $isNew: true, shop_id: this.getShopId },
      })
      this.editId = newRecord.id
    }, // insertEntity

    async updateEntity(id, payload) {
      this.Model.update({ where: id, data: { ...payload } })
    }, // updateEntity

    async updateCart(payload) {
      await CartModel.update(payload)
    }, // updateCart

    async createOrUpdateRecord(id) {
      this.formErrors = {}
      this.saving = true
      try {
        const theModel = await this.Model.find(id)
        await this.Model.createOrUpdate(id)
        this.closeEditDialog()

        if (theModel.$isNew) {
          this.reloadRecords()
        }
      } catch (error) {
        this.formErrors = _get(error, 'response.data.errors', {})
      } finally {
        this.saving = false
      }
    }, // createOrUpdateRecord

    async createNewOrder() {
      this.formErrors = {}
      this.saving = true
      try {
        await this.createOrderForAdmin()
        this.closeEditDialog()
        this.reloadRecords()
      } catch (error) {
        this.formErrors = _get(error, 'response.data.errors', {})
      } finally {
        this.saving = false
      }
    }, // createNewOrder

    async deleteEntity(id) {
      this.Model.delete(id)
    }, // deleteEntity

    openEditDialog(itemId) {
      this.editId = itemId
    },
    closeEditDialog() {
      this.formErrors = {}
      this.editId = false
    },
    setDialogState(id) {
      if (id) {
        this.openEditDialog(id)
      } else {
        this.closeEditDialog()
      }
    },
    closeNewRecordDialog() {
      this.showNewRecordDialog = false
    }, // closeNewRecordDialog
    reloadRecords() {
      this.$emit('show-snack', 'success', `${this.Model.nameSingular} Added`)
      this.isTableStale = true
    }, // reloadRecords
    _get,
  }, // methods
  created() {
    if (this.$route.query.sort) {
      this.tableSortDesc = this.$route.query.sort[0] === '-'
    }
    if (this.$route.params.id) {
      this.setDialogState(this.$route.params.id)
    }
  },
  watch: {
    '$route.params.id'(id) {
      this.setDialogState(id)
    },
  }, // watch
}
</script>

<style lang="scss">
.mm-base-table.sticky {
  > .v-data-table__wrapper > table > thead {
    .header-sticky {
      position: sticky;
      border-right: thin solid rgba(0, 0, 0, 0.12);
      left: 0px;

      z-index: 4;
      background: white;
    }
    .order-header-customer-full-name {
      left: 60px;
    }
  }
  .cell-sticky {
    position: sticky;
    left: 0;
    border-right: thin solid rgba(0, 0, 0, 0.12);
    z-index: 1;
    background: white;
  }
  .order-cell-customer-full-name {
    left: 60px;
  }
}
</style>
